/**
 * Exclusion & Inclusion Warning Messages
 *
 * All user-facing warning and status messages related to exclusions and inclusions
 *
 * IMPORTANT: The Metric Meter is a heuristic tool to assist in values selection
 * and portfolio planning. It provides indicative guidance based on market cap
 * coverage but does not guarantee or predict actual optimization outcomes.
 * Actual portfolio characteristics may vary.
 */

import { METRIC_METER_THRESHOLDS } from '@frontend/shared/metricMeterConstants';

// Re-export thresholds from centralized constants
export const THRESHOLDS = METRIC_METER_THRESHOLDS;

// Colors
export const COLORS = {
  RED: {
    text: '#dc2626',
    background: '#fee2e2',
    border: '#dc2626',
  },
  YELLOW: {
    text: '#d97706',
    background: '#fef3c7',
    border: '#f59e0b',
  },
  GREEN: {
    text: '#059669',
    background: '#d1fae5',
    border: '#10b981',
  },
};

// Diversification Status Messages (top of right sidebar)
export const DIVERSIFICATION_MESSAGES = {
  high: {
    main: (percentage) =>
      `⚠️ ${percentage.toFixed(1)}% of investable universe excluded. The metric meter is red because more than ${METRIC_METER_THRESHOLDS.RED}% of the investable universe is excluded. This indicates that an all-equity portfolio resulting from the selected screens has a higher likelihood of having a tracking error over 2% vs. the global equity benchmark. `,

    inclusionIssues: {
      high: 'Additionally, high levels of exclusions in one or more inclusion metrics may make it more challenging to improve those metrics over the benchmark during optimization.',
      moderate: 'Additionally, moderate to high levels of exclusions in one or more inclusion metrics could impact the ability to improve those metrics over the benchmark during optimization.',
    },
  },

  moderate: {
    main: (percentage) =>
      `⚠️ ${percentage.toFixed(1)}% of investable universe excluded. The metric meter is yellow because more than ${METRIC_METER_THRESHOLDS.YELLOW}% of the investable universe is excluded. This indicates that an all-equity portfolio resulting from the selected screens has a higher likelihood of having a tracking error over 1.75% vs. the global equity benchmark.`,

    inclusionIssues: {
      high: 'Additionally, high levels of exclusions in one or more inclusion metrics may make it more challenging to improve those metrics over the benchmark during optimization.',
      moderate: 'Additionally, moderate to high levels of exclusions in one or more inclusion metrics could impact the ability to improve those metrics over the benchmark during optimization.',
    },
  },

  good: {
    main: (percentage) =>
      `✓ ${percentage.toFixed(1)}% of investable universe excluded. The metric meter is green because less than ${METRIC_METER_THRESHOLDS.YELLOW}% of the investable universe is excluded.`,

    inclusionIssues: {
      high: '⚠️ However, high levels of exclusions in one or more inclusion metrics may make it more challenging to improve those metrics over the benchmark during optimization.',
      moderate: '⚠️ However, moderate to high levels of exclusions in one or more inclusion metrics could impact the ability to improve those metrics over the benchmark during optimization.',
    },
  },
};

// Inclusion Metric Breakdown Messages (when expanded)
export const INCLUSION_BREAKDOWN_MESSAGES = {
  high: '⚠️ High level of exclusions may present challenges for improving this metric over the benchmark during optimization',
  moderate: '⚠️ Moderate level of exclusions could impact the ability to improve this metric over the benchmark during optimization',
  good: '✓ The optimized portfolio is likely to score better than the benchmark on this metric',
};

// Difficult Metrics
export const DIFFICULT_METRICS = {
  buttonTooltip: 'Show difficult metrics (metrics that may exclude significant market cap and increase tracking error)',
  iconTooltip: 'Difficult metric - may exclude significant market cap and increase tracking error',
};

// Section Headers
export const SECTION_HEADERS = {
  exclusionMetrics: 'Exclusion Metrics',
  inclusionMetrics: 'Inclusion Metrics',

  breakdownHeaders: {
    metric: 'METRIC',
    additional: 'ADDITIONAL',
    cumulativeMktCap: 'CUML. MKT CAP',
  },

  inclusionBreakdown: {
    exclusionMetric: 'EXCLUSION METRIC',
    additional: 'ADDITIONAL',
    cumulativeMktCap: 'CUML. MKT CAP',
  },
};

/**
 * Helper function to get diversification message based on exclusion level and inclusion issues
 * @param {number} percentageExcluded - Total percentage of market cap excluded
 * @param {number} highInclusionCount - Number of inclusion metrics with high exclusions (>38%)
 * @param {number} moderateInclusionCount - Number of inclusion metrics with moderate exclusions (20-38%)
 * @returns {object} - Object with main message and optional inclusion issues message
 */
export function getDiversificationMessage(percentageExcluded, highInclusionCount, moderateInclusionCount) {
  let level;
  if (percentageExcluded > METRIC_METER_THRESHOLDS.RED) {
    level = 'high';
  } else if (percentageExcluded >= METRIC_METER_THRESHOLDS.YELLOW) {
    level = 'moderate';
  } else {
    level = 'good';
  }

  const messageSet = DIVERSIFICATION_MESSAGES[level];
  const result = {
    main: messageSet.main(percentageExcluded),
    inclusionIssue: null,
  };

  if (highInclusionCount > 0) {
    result.inclusionIssue = messageSet.inclusionIssues.high;
  } else if (moderateInclusionCount > 0) {
    result.inclusionIssue = messageSet.inclusionIssues.moderate;
  }

  return result;
}

/**
 * Helper function to get inclusion breakdown message based on exclusion percentage
 * @param {number} percentageExcluded - Percentage of market cap excluded for this inclusion metric
 * @returns {string} - The appropriate message
 */
export function getInclusionBreakdownMessage(percentageExcluded) {
  if (percentageExcluded > METRIC_METER_THRESHOLDS.RED) {
    return INCLUSION_BREAKDOWN_MESSAGES.high;
  } else if (percentageExcluded >= METRIC_METER_THRESHOLDS.YELLOW) {
    return INCLUSION_BREAKDOWN_MESSAGES.moderate;
  } else {
    return INCLUSION_BREAKDOWN_MESSAGES.good;
  }
}

/**
 * Helper function to get color scheme based on exclusion percentage
 * @param {number} percentageExcluded - Percentage of market cap excluded
 * @returns {object} - Color object with text, background, and border colors
 */
export function getColorScheme(percentageExcluded) {
  if (percentageExcluded > METRIC_METER_THRESHOLDS.RED) {
    return COLORS.RED;
  } else if (percentageExcluded >= METRIC_METER_THRESHOLDS.YELLOW) {
    return COLORS.YELLOW;
  } else {
    return COLORS.GREEN;
  }
}
